import React, { useEffect, useState } from "react";
import { Button, Card, Row } from "react-bootstrap";
import { DeviceBase, IActionHandler, OrgInfo, DeviceCertificate, CollectionServerUser } from "@preveil-api";
import {
  Account, AdminErrorMessages, AdminMessages, AdminSuccessMessages, BOM, CERTIFICATE_HEADERS, DeviceObject, GetCertificateDetailsApiResponse, GlobalErrorMessages, Message,
  MessageAnchors, MessageHandlerDisplayType, MessageToastTypes, PutOrgDeviceCertificatesApiResponse, SupportRoutes, UUID, collectionApi, dayjs, useAppDispatch, useAppSelector,
  useDeleteOrgDeviceCertificatesMutation, useGetCertificateDetailsMutation, usePostUsersOrgsByEntityIdAllowAddDeviceMutation, usePutOrgDeviceCertificatesMutation
} from "src/common";
import { AlertWithSwitch, DefaultTemplate, EmptyView, Icon, PageHeader } from "src/components";
import { AddCertificate, ListView, Toolbar, ToggleChangeWarning } from ".";
import { adminActions, uiActions } from "src/store";
import { RootState } from "src/store/configureStore";
import saveAs from "file-saver";
import _ from "lodash";

type AllProps = {
  current_account: Account;
  org_info: OrgInfo;
}

// Description: https://preveil.atlassian.net/wiki/spaces/ESD/pages/3837591568/Admin+Console+-+Device+Settings
function DeviceSettingsComponent(props: AllProps) {
  const { current_account, org_info } = props;
  const dispatch = useAppDispatch();
  const selected = useAppSelector((state: RootState) => state.admin.selected);
  const [is_loading, setIsLoading] = useState<boolean>(true);
  const [org_users, setOrgUsers] = useState<CollectionServerUser[]>([]);
  const [certificates, setCertificates] = useState<DeviceCertificate[]>([]);
  const [filtered_list, setFilteredList] = useState<DeviceCertificate[]>([]);
  const [filter_param, setFilterParam] = useState<string>("");
  const [device_lock_status, setDeviceLockStatus] = useState<boolean>(false);
  const [show_add_certificate_modal, setShowAddCertificateModal] = useState<boolean>(false);
  const [show_warning_device_modal, setShowWarningDeviceModal] = useState<boolean>(false);
  const [getOrgDeviceCertificates] = collectionApi.endpoints.getOrgDeviceCertificates.useLazyQuery();
  const [getUsersOrgsByEntityId] = collectionApi.endpoints.getUsersOrgsByEntityId.useLazyQuery();
  const [postUsersOrgsByEntityIdAllowAddDevice] = usePostUsersOrgsByEntityIdAllowAddDeviceMutation();
  const [uploadDeviceCertificate] = usePutOrgDeviceCertificatesMutation();
  const [deleteDeviceCertificate] = useDeleteOrgDeviceCertificatesMutation();
  const [getCertificateDetails] = useGetCertificateDetailsMutation();

  // Description: On load
  useEffect(() => {
    getDeviceSettingsState();
  }, []);

  // Description: Fetches the initial state of the device settings
  async function getDeviceSettingsState() {
    getUsersOrgsByEntityId({
      account_ids: Account.getAccountIdentifiers(current_account),
      body: {
        entity_id: org_info.org_id
      }
    })
      .unwrap()
      .then(({ allow_add_devices, users }) => {
        setDeviceLockStatus(allow_add_devices || false);
        setOrgUsers(users);
      })
      .catch((error) => {
        const params = {
          message: AdminErrorMessages.error_admin_settings_data,
          stack: error
        };
        DeviceSettingRequests.handlePageErrorMessage(params);
      })
      .finally(() => {
        fetchCertificates();
      });
  }

  // Description: Fetches the certificates for the current organization
  async function fetchCertificates() {
    await getOrgDeviceCertificates({
      account_ids: Account.getAccountIdentifiers(current_account),
      body: {
        entity_id: org_info.org_id
      }
    })
      .unwrap()
      .then(({ rules }) => {
        if (rules.length > 0) {
          const certificate_details_promises = rules.map(rule =>
            getCertificateDetails({ certificate_body: rule.specifier_body }).unwrap()
          );

          Promise.all(certificate_details_promises.map((promise, index) =>
            promise.then((response: GetCertificateDetailsApiResponse) => ({
              ...typeof response === "string" ? JSON.parse(response) : response,
              id: rules[index].acl_id
            }))
          ))
            .then((parsed_certificates: DeviceCertificate[]) => {
              setCertificates(parsed_certificates);
              setFilteredList(parsed_certificates);
              setIsLoading(false);
            })
            .catch((error) => {
              const params = {
                message: AdminErrorMessages.error_admin_parse_device_certificate,
                stack: error
              };
              DeviceSettingRequests.handlePageErrorMessage(params);
              // If there's an error fetching certificate details, set certificates to empty
              clearState();
            });
        } else {
          // If there are no rules, set certificates to empty
          clearState();
        }
      })
      .catch((error) => {
        const params = {
          message: AdminErrorMessages.error_admin_settings_data,
          stack: error
        };
        DeviceSettingRequests.handlePageErrorMessage(params);
        // In case of any error in the try block, set certificates to empty
        clearState();
      });
  }

  // Description: Handles Request for User Device Management.
  async function toggleUserDeviceManagementRequest() {
    const is_allowed = !device_lock_status;
    setShowWarningDeviceModal(false);
    await postUsersOrgsByEntityIdAllowAddDevice({
      account_ids: Account.getAccountIdentifiers(current_account),
      body: {
        entityId: org_info.org_id,
        is_allowed
      }
    })
      .unwrap()
      .then(() => {
        setDeviceLockStatus(is_allowed);
      })
      .catch((error) => {
        const params = {
          message: AdminErrorMessages.error_admin_settings_switch_options.replace(MessageAnchors.message_content, "changing user device management options"),
          stack: error
        };
        DeviceSettingRequests.handlePageErrorMessage(params);
      });
  };

  function handleToggleUserDeviceManagement() {
    if (device_lock_status) {
      setShowWarningDeviceModal(true);
    } else {
      toggleUserDeviceManagementRequest();
    }
  }

  function clearState() {
    setCertificates([]);
    setFilteredList([]);
    setIsLoading(false);
  }

  // Description: returns a single device object with the columns data for the CSV file.
  function createDeviceObject(user_ata: { user_id: string, display_name: string }, device_data: DeviceBase): DeviceObject {
    const { user_id, display_name: user_name } = user_ata;
    const { device_id, platform, device_name, is_active, lock_info, } = device_data;
    const active = is_active ? "Yes" : "No";
    const { lock_by_user_id: locked_by, lock_time } = lock_info || {};
    return {
      user_id,
      device_name,
      device_id,
      user_name,
      platform,
      active,
      locked_by,
      lock_time
    };
  };

  // Description: Export devices list as a CSV file
  function handleExportDevicesList() {
    try {
      const devices_array = org_users
        .filter((user: CollectionServerUser) => !user.deleted && Array.isArray(user.devices) && user.devices.length > 0)
        .flatMap((user: CollectionServerUser) => {
          const user_ata: { user_id: string, display_name: string } = {
            user_id: user.user_id,
            display_name: user.display_name
          };
          return user.devices.map((device: DeviceBase) => createDeviceObject(user_ata, device));
        });

      if (devices_array.length === 0) {
        // In the rare case that no devices are found, throw an error that will be caught in the catch block.
        throw new Error("No devices found to export.");
      }

      const csv_string = [
        [...CERTIFICATE_HEADERS],
        ...devices_array.map((device: DeviceObject) => [
          device.user_id,
          device.device_name,
          device.device_id,
          device.user_name,
          device.platform,
          device.active,
          device.locked_by,
          device.lock_time
        ])
      ]
        .map(row => row.join(","))
        .join("\n");

      const csv_data = `${BOM}${csv_string}`;
      const blob = new Blob([csv_data], { type: "text/csv; charset=utf-8" });
      saveAs(blob, `users-${dayjs(new Date()).format("MM.DD.YYYY-HH_mm_ss")}.csv`);
    } catch (error) {
      const params = {
        message: AdminErrorMessages.error_admin_settings_export_csv,
        stack: error
      };
      DeviceSettingRequests.handlePageErrorMessage(params);
    };
  };

  function deleteCertificates() {
    setIsLoading(true);
    const account_ids = Account.getAccountIdentifiers(current_account);
    const req_params = {
      account_ids,
      body: {
        entity_id: org_info.org_id,
        acl_ids: selected,
      }
    };
    deleteDeviceCertificate(req_params)
      .unwrap()
      .then((_: PutOrgDeviceCertificatesApiResponse) => {
        dispatch(adminActions.setSelected([]));
        fetchCertificates();
        dispatch(uiActions.handleSetMessage(new Message(AdminSuccessMessages.success_deleted_certificate)));
      })
      .catch((error) => {
        const params = {
          message: AdminErrorMessages.error_admin_deleting_device_certificate,
          stack: error
        };
        DeviceSettingRequests.handlePageErrorMessage(params);
        setIsLoading(false);
      });
  }

  const DeviceSettingRequests = {
    handleShowModal: () => {
      setShowAddCertificateModal(!show_add_certificate_modal);
    },
    handleSetShow: () => {
      setShowWarningDeviceModal(!show_warning_device_modal);
    },
    handleConfirm: () => {
      toggleUserDeviceManagementRequest();
    },
    handleSelect: (params: { selected: boolean, certificate?: DeviceCertificate }) => {
      let new_selected = selected.slice();
      if (!!params.certificate) { // Note: individual check
        if (params.selected) {
          !_.find(new_selected, (_selected: string) => (_selected === params.certificate?.id)) &&
            new_selected.push(params.certificate.id);
        } else {
          const index = _.findIndex(new_selected, (_selected: string) => (_selected === params.certificate?.id));
          index >= 0 && (new_selected.splice(index, 1));
        }
      } else {
        new_selected = params.selected ? filtered_list.map(cert => cert.id) : [];
      }
      dispatch(adminActions.setSelected(new_selected));
    },
    handleSearchParam: (param?: string) => {
      if (!!param) {
        setFilterParam(param.toLowerCase());
        const filtered = certificates.filter((certificate: DeviceCertificate) => {
          return (
            Object.values(certificate).some((value: unknown) =>
              (value?.toString() ?? "").toLowerCase().includes(param.toLowerCase())
            )
          );
        });
        setFilteredList(filtered);
      } else {
        setFilterParam("");
        setFilteredList(certificates);
      }
    },
    handleDelete: () => {
      const confirmation_dialog = new Message("Are you sure you want to remove the selected certificates?", MessageHandlerDisplayType.confirm, MessageToastTypes.primary, "Delete Device Certificate(s)",
        {
          label: "Yes",
          data: true,
          action: () => deleteCertificates(),
        },
        { label: "No" },
      );
      dispatch(uiActions.handleSetMessage(confirmation_dialog));
    },
    handleGetRefreshRequest: () => {
      setIsLoading(true);
      fetchCertificates();
    },
    handlePageErrorMessage: (params: { message: string, stack?: any }) => {
      dispatch(uiActions.handleRequestErrors(new Message(params.message, MessageHandlerDisplayType.toastr), params.stack));
    },
    // Description: Handles Request for Uploading Device Certificates
    handleUploadDeviceCertificate: (params: { certificate: string }) => {
      setIsLoading(true);
      const account_ids = Account.getAccountIdentifiers(current_account);
      const req_params = {
        account_ids,
        body: {
          entity_id: org_info.org_id,
          acl_id: new UUID().String(),
          specifier_type: "certificate",
          specifier_body: params.certificate,
        }
      };
      uploadDeviceCertificate(req_params)
        .unwrap()
        .then((_: PutOrgDeviceCertificatesApiResponse) => {
          fetchCertificates();
          dispatch(uiActions.handleSetMessage(new Message(AdminSuccessMessages.success_uploaded_certificate)));
        })
        .catch((error) => {
          const params = {
            message: AdminErrorMessages.error_admin_uploading_device_certificate,
            stack: error
          };
          DeviceSettingRequests.handlePageErrorMessage(params);
          setIsLoading(false);
        });
    }
  };

  // Description: Handle all actions from Children forms
  function handlePageActions(action_obj: IActionHandler) {
    dispatch(uiActions.handleMessageDismiss());
    const callback = `handle${action_obj.actionType}`;
    // Handle local calls:
    if ((DeviceSettingRequests as any)[callback] instanceof Function) {
      (DeviceSettingRequests as any)[callback](action_obj.params);
    } else {
      const message = GlobalErrorMessages.no_handler_found.replace(MessageAnchors.actionType, action_obj.actionType);
      DeviceSettingRequests.handlePageErrorMessage({ message, stack: action_obj });
    }
  }

  return <DefaultTemplate className="admin-component">
    <PageHeader>
      <h1>Device Settings</h1>
    </PageHeader>
    <Card className="card-section">
      <h3>User Device Management</h3>
      <AlertWithSwitch
        popover={true}
        popover_message={device_lock_status ? AdminMessages.device_settings.enabled_hint : AdminMessages.device_settings.disabled_hint}
        toggleState={device_lock_status}
        handleChange={handleToggleUserDeviceManagement} >
        <span dangerouslySetInnerHTML={{ __html: device_lock_status ? AdminMessages.device_settings.enabled : AdminMessages.device_settings.disabled }} />
      </AlertWithSwitch>
      <hr />
      <h3>Trusted Devices</h3>
      <div className="d-flex">
        <p>Add a <b>Trusted Root Certificate</b> requirement to restrict user access.</p>
        <a target="_blank" rel="noreferrer" href={SupportRoutes.device_certificate}>
          <Icon className="pv-icon-question text-muted" />
        </a>
      </div>
      <Toolbar filter_param={filter_param} certificates={certificates} filtered_certificates={filtered_list} is_loading={is_loading} handleAction={handlePageActions} />
      {certificates.length === 0 && !is_loading ?
        <EmptyView message="No Device Certificates" /> :
        <div id="outer-scope" className="cover-content">
          <ListView certificates={filtered_list} is_loading={is_loading} selected={selected} handleAction={handlePageActions} />
        </div>}

      <AddCertificate
        show_modal={show_add_certificate_modal}
        handleAction={handlePageActions} />
      <ToggleChangeWarning
        show_modal={show_warning_device_modal}
        handleAction={handlePageActions} />
      <hr />
      <Row>
        <h3 className="pt-3">Organization Device List</h3>
        <div className="align-center">
          <Button className="me-2" onClick={handleExportDevicesList}>Export</Button>
          <p className="mb-0">Export information about your current organization members' devices into a .csv file.</p>
        </div>
      </Row>
    </Card>
  </DefaultTemplate>;
}

export default React.memo(DeviceSettingsComponent);
